import { printReceipt, POPUP_BLOCKED } from '@matix/receipt';

/** Which printer a report is going to. */
export type ReportPaper = 'roll' | 'a4';

/**
 * Print a report table, on the till's receipt printer or on an A4 sheet.
 *
 * Both go through `printReceipt`, so the page opens, prints and closes
 * exactly like a receipt does; only the stylesheet and the table treatment
 * differ.
 *
 * `roll` — the receipt's own 72 mm band, the one `buildReceiptHtml` prints
 * on. The reports once printed as an A4 sheet only; on the 80 mm roll the
 * counter actually has, the browser shrank that sheet to fit and "Sales by
 * Salesman" came out as a 2 cm strip of unreadable text in the middle of a
 * 30 cm feed. A table that is too wide for the roll (five columns or more)
 * is re-flowed into one block per row — the first column as a heading, every
 * other column as a "label … value" line — which is how a receipt lays the
 * same numbers out. Narrow tables (type / qty / amount) stay tables.
 *
 * `a4` — a full sheet with the tables kept as tables, sized for the office
 * printer. The roll stylesheet on an A4 printer gives the mirror-image
 * failure: a 72 mm strip of small type down the middle of a blank page.
 */
export function printReport(
  title: string,
  subtitle: string,
  bodyHtml: string,
  paper: ReportPaper = 'roll',
  opts: { landscape?: boolean } = {},
): void {
  const isAr = document.documentElement.getAttribute('lang') === 'ar';
  const a4 = paper === 'a4';
  const html = `<!doctype html>
<html lang="${isAr ? 'ar' : 'en'}" dir="${document.documentElement.getAttribute('dir') ?? 'ltr'}">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>${a4 ? A4_CSS + (opts.landscape ? A4_LANDSCAPE_CSS : '') : ROLL_CSS}</style>
</head>
<body>
<div class="rpt-head"><h1>${escapeHtml(title)}</h1><div class="sub">${escapeHtml(subtitle)}</div></div>
${a4 ? bodyHtml : fitToRoll(bodyHtml)}
</body>
</html>`;

  void printReceipt(html).catch((err: unknown) => {
    if (err instanceof Error && err.message === POPUP_BLOCKED) {
      window.alert(isAr
        ? 'تم حظر النافذة المنبثقة. اسمح بالنوافذ المنبثقة لهذا الموقع لطباعة التقرير.'
        : 'Pop-up blocked. Allow pop-ups for this site to print the report.');
    }
  });
}

/** A table with this many columns or more no longer fits the roll as a table. */
const STACK_FROM_COLUMNS = 5;

/**
 * Re-flow every wide table in `bodyHtml` into row blocks (see `printReport`).
 * Pure string → string; the DOM it builds is a detached parser document.
 */
export function fitToRoll(bodyHtml: string): string {
  if (typeof DOMParser === 'undefined') return bodyHtml;
  const doc = new DOMParser().parseFromString(`<div id="rpt-root">${bodyHtml}</div>`, 'text/html');
  const root = doc.getElementById('rpt-root');
  if (!root) return bodyHtml;

  for (const table of Array.from(root.querySelectorAll('table'))) {
    const labels = Array.from(table.querySelectorAll('thead th'))
      .map((th) => th.textContent?.trim() ?? '');
    if (labels.length < STACK_FROM_COLUMNS) continue;

    const cards = doc.createElement('div');
    cards.className = 'rpt-cards';
    for (const tr of Array.from(table.querySelectorAll('tbody tr, tfoot tr'))) {
      const cells = Array.from(tr.children);
      const card = doc.createElement('div');
      card.className = tr.parentElement?.tagName === 'TFOOT' ? 'rpt-card total' : 'rpt-card';

      // A single spanning cell is a message ("No data"), not a row of numbers —
      // except a modifier breakdown, which belongs TO the card above it rather
      // than beside it. Appending it there keeps "Gathering Box" and the
      // sandwiches that were chosen inside it together on the roll.
      if (cells.length <= 1) {
        const text = cells[0]?.textContent?.trim() ?? '';
        if (tr.classList.contains('rpt-subrow')) {
          const previous = cards.lastElementChild;
          if (previous) {
            const sub = doc.createElement('div');
            sub.className = 'rpt-card-mods';
            sub.textContent = text;
            previous.appendChild(sub);
            continue;
          }
        }
        card.classList.add('single');
        card.textContent = text;
        cards.appendChild(card);
        continue;
      }

      const head = doc.createElement('div');
      head.className = 'rpt-card-head';
      head.textContent = cells[0]?.textContent?.trim() ?? '';
      card.appendChild(head);

      const isTotal = tr.parentElement?.tagName === 'TFOOT';
      cells.slice(1).forEach((td, i) => {
        // A totals row leaves the text columns blank (no total for a date or
        // a name); on the roll those would print as empty "label …" lines.
        if (isTotal && !(td.textContent ?? '').trim()) return;
        const kv = doc.createElement('div');
        kv.className = 'rpt-kv';
        const k = doc.createElement('span');
        k.className = 'k';
        k.textContent = labels[i + 1] ?? '';
        const v = doc.createElement('span');
        v.className = 'v';
        v.textContent = td.textContent?.trim() ?? '';
        kv.append(k, v);
        card.appendChild(kv);
      });
      cards.appendChild(card);
    }
    table.replaceWith(cards);
  }
  return root.innerHTML;
}

/* Same band and inner padding as the receipt (`buildReceiptHtml`): an 80 mm
   head images ~72 mm and clips the rest. */
const ROLL_CSS = `
  @page { size: 72mm auto; margin: 0; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0; width: 72mm; background: #fff; color: #000;
    font-family: Tahoma, "Geeza Pro", "Noto Sans Arabic", Arial, sans-serif;
    font-size: 11px; line-height: 1.35;
  }
  body { padding: 4mm 3mm; }
  .rpt-head { text-align: center; margin-bottom: 6px; }
  .rpt-head h1 { font-size: 14px; margin: 0 0 2px; }
  .rpt-head .sub { font-size: 10px; }
  .rpt-meta { text-align: center; font-size: 10px; margin-bottom: 4px; }
  h2.section {
    font-size: 12px; margin: 8px 0 2px; padding-bottom: 2px;
    border-bottom: 1px solid #000;
  }
  table { width: 100%; border-collapse: collapse; margin: 2px 0 6px; }
  th, td {
    padding: 2px 2px; border: 0; border-bottom: 1px dotted #888;
    font-size: 10px; vertical-align: top; overflow-wrap: anywhere;
  }
  th { border-bottom: 1px solid #000; text-align: start; font-weight: 700; }
  td.num, th.num { text-align: end; font-variant-numeric: tabular-nums; white-space: nowrap; }
  tfoot td, .total-row td { font-weight: 700; border-top: 1px solid #000; border-bottom: 0; }
  /* Modifier breakdown: part of the card above it, so it prints tight. */
  .rpt-card-mods { font-size: 9px; padding: 1px 0 0 6px; line-height: 1.35; }
  html[dir="rtl"] .rpt-card-mods { padding: 1px 6px 0 0; }
  tr.rpt-subrow td { font-size: 9px; padding-left: 8px; border-bottom: 1px dotted #888; }
  .rpt-empty { text-align: center; font-size: 10px; padding: 4px 0; }
  .rpt-cards { margin: 2px 0 6px; }
  .rpt-card { padding: 3px 0; border-bottom: 1px dashed #000; }
  .rpt-card.single { text-align: center; font-size: 10px; border-bottom: 0; }
  .rpt-card.total { border-top: 2px solid #000; border-bottom: 0; font-weight: 700; }
  .rpt-card-head { font-weight: 700; margin-bottom: 1px; overflow-wrap: anywhere; }
  .rpt-kv { display: flex; justify-content: space-between; gap: 6px; font-size: 10px; }
  .rpt-kv .k { flex: 0 1 auto; min-width: 0; }
  .rpt-kv .v {
    flex: 1 1 auto; text-align: end; white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

/* Full sheet. The tables stay tables — there is room for every column — and
   the two-column report layout (`.rpt-two-col`) is kept, since side-by-side
   is what an A4 page has the width for. */
const A4_CSS = `
  @page { size: A4; margin: 15mm 14mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0; padding: 0; background: #fff; color: #000;
    font-family: Tahoma, "Segoe UI", "Noto Sans Arabic", Arial, sans-serif;
    font-size: 13px; line-height: 1.4;
  }
  .rpt-head { text-align: center; margin-bottom: 14px; padding-bottom: 8px; border-bottom: 2px solid #000; }
  .rpt-head h1 { font-size: 20px; margin: 0 0 4px; }
  .rpt-head .sub { font-size: 12px; color: #333; }
  .rpt-meta { font-size: 12px; color: #333; margin-bottom: 8px; }
  h2.section {
    font-size: 14px; margin: 16px 0 6px; padding-bottom: 3px;
    border-bottom: 1px solid #000; break-after: avoid;
  }
  .rpt-two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; align-items: start; }
  table { width: 100%; border-collapse: collapse; margin: 4px 0 12px; font-size: 12px; }
  th, td { border: 1px solid #000; padding: 5px 8px; vertical-align: top; }
  th { background: #eee; text-align: start; font-weight: 700; }
  td.num, th.num { text-align: end; font-variant-numeric: tabular-nums; white-space: nowrap; }
  tfoot td, .total-row td { font-weight: 700; background: #f5f5f5; }
  /* Options chosen inside an item: indented, no box, never split off. */
  tr.rpt-subrow td { font-size: 11px; padding: 3px 8px 5px 22px; border-top: 0; background: #fafafa; }
  html[dir="rtl"] tr.rpt-subrow td { padding: 3px 22px 5px 8px; }
  .rpt-mods-label { font-weight: 600; }
  tr { break-inside: avoid; }
  .rpt-empty { text-align: center; color: #555; padding: 8px 0; }
  html[dir="rtl"] th { text-align: right; }
`;

/* A line-level report (Returned Items) carries a dozen-plus columns: turn the
   sheet and tighten the cells so every one still fits across the page. The
   later `@page` wins over the portrait one in A4_CSS. */
const A4_LANDSCAPE_CSS = `
  @page { size: A4 landscape; margin: 10mm 8mm; }
  table { font-size: 9.5px; }
  th, td { padding: 3px 4px; }
`;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>
  )[c]!);
}
